import classnames from 'classnames'
import {
    groupBy,
    pipe,
    converge,
    identity,
    flip,
    curryN,
    objOf,
    mergeRight,
    subtract,
    flatten
} from 'ramda'
import {
    getDate,
    isSameDay,
    isToday,
    format,
    subMonths,
    addMonths,
    isSameMonth,
    addDays,
    startOfMonth,
    subDays,
    getDay,
    endOfDay,
    endOfMonth,
    eachDayOfInterval
} from 'date-fns'

const addDaysToDate = flip(curryN(2, addDays))
const getRangeStart = pipe(
    startOfMonth,
    converge(subDays, [identity, getDay])
)

const getRangeEnd = pipe(
    endOfMonth,
    converge(addDays, [identity, pipe(getDay, subtract(6))]),
    endOfDay
)

const getDateRange = converge(mergeRight, [
    pipe(getRangeStart, objOf('start')),
    pipe(getRangeEnd, objOf('end'))
])

const getDaysOfTargetRange = pipe(getDateRange, eachDayOfInterval)

export function EventsCalendar(props = {}) {
    const {
        events = [],
        targetDate = new Date(),
        onTargetDateChange,
    } = props;


    return (
        <>
            <div class="grid grid-cols-3 items-center justify-content-between justify-items-center">
                <button class="justify-self-start font-semibold text-gray-dark text-2xl flex items-center" onClick={() => onTargetDateChange(subMonths(targetDate, 1))}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" id="chevronleft" class="fill-current w-6"><path d="M34.2 47.7L13.4 27.2c-.6-.6-.6-1.6 0-2.2L34.2 4.5c.6-.6 1.6-.6 2.2 0l2.2 2.2c.6.6.6 1.6 0 2.2L22.1 25c-.6.6-.6 1.6 0 2.2l16.3 16.1c.6.6.6 1.6 0 2.2l-2.2 2.2c-.5.5-1.4.5-2 0z"></path></svg>
                    {format(subMonths(targetDate, 1), 'MMMM YYY')}
                </button>
                <h2>{format(targetDate, 'MMMM YYY')}</h2>
                <button class="justify-self-end font-semibold text-gray-dark text-2xl flex items-center" onClick={() => onTargetDateChange(addMonths(targetDate, 1))}>
                    {format(addMonths(targetDate, 1), 'MMMM YYY')}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" id="chevronright" class="fill-current w-6"><path d="M17.9 4.4l20.7 20.5c.6.6.6 1.6 0 2.2L17.9 47.6c-.6.6-1.6.6-2.2 0l-2.2-2.2c-.6-.6-.6-1.6 0-2.2l16.3-16.1c.6-.6.6-1.6 0-2.2L13.6 8.8c-.6-.6-.6-1.6 0-2.2l2.2-2.2c.6-.5 1.5-.5 2.1 0z"></path></svg>
                </button>
            </div>
            <div class="bg-gray-lightest gap-1 grid grid-cols-7 p-1" style={{
                gridTemplateRows: 'min-content repeat(5, 1fr)'
            }}>
                <ul class="contents list-none">
                    <li class="bg-white font-semibold m-0 p-0 self-start text-center uppercase">Sun</li>
                    <li class="bg-white font-semibold m-0 p-0 self-start text-center uppercase">Mon</li>
                    <li class="bg-white font-semibold m-0 p-0 self-start text-center uppercase">Tue</li>
                    <li class="bg-white font-semibold m-0 p-0 self-start text-center uppercase">Wed</li>
                    <li class="bg-white font-semibold m-0 p-0 self-start text-center uppercase">Thu</li>
                    <li class="bg-white font-semibold m-0 p-0 self-start text-center uppercase">Fri</li>
                    <li class="bg-white font-semibold m-0 p-0 self-start text-center uppercase">Sat</li>
                </ul>
                <ul class="contents list-none">
                    {getDaysOfTargetRange(targetDate).map(date =>

                        <li class="bg-white m-0 p-0 text-center">
                            <div class="gap-2 grid items-start justify-items-center p-2" style={{
                                gridTemplateRows: 'min-content 1fr'
                            }}
                            >
                                <div class={classnames(
                                    'leading-normal p-1 rounded-full h-8',
                                    {
                                        'bg-blue': isToday(date),
                                        'text-white': isToday(date),
                                        'font-semibold': isToday(date),
                                        'text-gray-darkest': !isToday(date) && isSameMonth(targetDate, date),
                                        'text-gray-dark': !isToday(date) && !isSameMonth(targetDate, date)
                                    }
                                )} style={{ aspectRatio: '1/1' }}>{getDate(date)}</div>
                                <div class="gap-2 grid justify-self-start content-start max-w-full relative text-left" style={{ aspectRatio: '1/1' }}>
                                    {
                                        (events || [])
                                            .filter(event => isSameDay(new Date(event.meta.eventStartDate), date))
                                            .map(event => (
                                                <a href={event.link} class="events-calendar__item font-semibold text-base relative z-0 hover:z-50 group">
                                                    <span class="whitespace-nowrap overflow-hidden">{event?.title?.raw || event?.title?.rendered}</span>
                                                    <div class="bg-white p-6 absolute hidden group-hover:block 
                                                    shadow-lg
                                                    rounded
                                                    w-96
                                                    opacity-0
                                                    transition-opacity
                                                    duration-500 
                                                    ease-in-out
                                                    group-hover:opacity-100
                                                    events-calendar__item-details">
                                                        <h3 class="text-2xl">{event?.title?.raw || event?.title?.rendered}</h3>
                                                        <ul class="list-none text-gray-darkest">
                                                            <li class="ml-0"><strong>Category:</strong> <a href="#">{flatten(event._embedded['wp:term']).find(i => i.taxonomy === 'event_types').name}</a> </li>
                                                            <li class="ml-0"><strong>When:</strong> {format(new Date(event.meta.eventStartDate), 'MMMM d, pp')}</li>
                                                            <li class="ml-0"><strong>Where:</strong> {event.meta.eventLocation}</li>
                                                        </ul>
                                                        <p>{event?.excerpt?.raw || event?.excerpt?.rendered}</p>
                                                        <a href={event.link} class="text-blue text-base inline-grid gap-2 grid-col-2-maxcontent items-center"><span>Learn more</span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" id="forward" class="fill-current w-3.5 h-3.5"><path d="M3.4 29h33.2c.9 0 1.3 1.1.7 1.7l-9.6 9.6c-.6.6-.6 1.5 0 2.1l2.2 2.2c.6.6 1.5.6 2.1 0L49.5 27c.6-.6.6-1.5 0-2.1L32 7.4c-.6-.6-1.5-.6-2.1 0l-2.1 2.1c-.6.6-.6 1.5 0 2.1l9.6 9.6c.6.7.2 1.8-.7 1.8H3.5c-.8 0-1.5.6-1.5 1.4v3c0 .8.6 1.6 1.4 1.6z"></path></svg></a>
                                                    </div>
                                                </a>
                                            ))
                                    }
                                </div>

                            </div></li>
                    )}

                </ul>
            </div>
        </>
    )
}

export default EventsCalendar;